import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import {
  dataFieldsService,
  FeedDataFieldType,
  FieldType,
  Ranking,
  RankingStrategyTypes,
  ShopDataField,
  SyteProductTypes,
} from 'src/services';
import { getProductName } from 'src/components-bl/MerchandisingRules/merchandisingRules.helpers';
import { RankingWeightRecord } from 'src/components-bl/RankingStrategy/RankingStrategyTable/types';
import { syteCoreValuesSet } from 'src/components-bl/RankingStrategy/RankingStrategyTable/constants';
import { RankingType } from 'src/components-bl/RankingStrategy/RankingStrategyType/types';
import {
  NewRankingParameter,
  RankingStrategyEditorProps,
  RankingWeightRow,
  UseRankingStrategyEditorReturnType,
} from './types';

const rankingService = new Ranking();
const PERSIST_DEBOUNCE_MS = 400;

export const useRankingStrategyEditor = ({
  shopId,
  surface,
  controlVariantId,
  testVariantId,
  onChangeCountChange,
  onSaveReady,
}: RankingStrategyEditorProps): UseRankingStrategyEditorReturnType => {
  // The FE `SyteProductType` enum used for wizard lever selection and the one used by the
  // ranking module are separate (but value-identical) declarations - see
  // project_syte_product_type_enum_mismatch memory. Safe to bridge with a cast here.
  const syteProduct = surface as unknown as SyteProductTypes.SyteProductType;

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [controlWeights, setControlWeights] = useState<RankingWeightRecord[]>();
  const [draftWeights, setDraftWeights] = useState<RankingWeightRecord[]>();
  const [type, setType] = useState<RankingType>();
  const [dataFields, setDataFields] = useState<ShopDataField[]>();

  const loadWeights = useCallback((): Promise<void> => {
    if (!controlVariantId || !testVariantId) return Promise.resolve();

    setIsLoading(true);
    setHasError(false);
    return Promise.all([
      rankingService.get({ shopId, syteProduct, variantId: controlVariantId }),
      rankingService.get({ shopId, syteProduct, variantId: testVariantId }),
      dataFieldsService.getDataFields({ shopId, fieldType: FieldType.CatalogField }),
    ])
      .then(([controlResult, testResult, fetchedDataFields]) => {
        setControlWeights(controlResult.weights);
        setDraftWeights(testResult.weights);
        setType(testResult.type);
        setDataFields(fetchedDataFields);
      })
      .catch(() => {
        setControlWeights(undefined);
        setDraftWeights(undefined);
        setDataFields(undefined);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [shopId, syteProduct, controlVariantId, testVariantId]);

  useEffect(() => {
    loadWeights();
  }, [loadWeights]);

  // Every edit here writes straight to the Test variant's own ranking-strategy record (the same
  // CRUD endpoint the non-wizard variant editor uses) - never to Control or production. There is
  // no separate wizard "patch" object for this lever; per project_wizard_step3_patch_model_scope,
  // step 3 persists through the existing variant CRUD, debounced so dragging a slider doesn't
  // fire a request per pixel. `pendingWeightsRef` tracks the latest not-yet-persisted value so
  // Continue can flush it immediately instead of losing it when the debounce is cancelled on
  // unmount (leaving step 3).
  const pendingWeightsRef = useRef<RankingWeightRecord[] | null>(null);

  const sendUpdate = useCallback(
    (weightsToSave: RankingWeightRecord[]): Promise<boolean> => {
      if (!testVariantId) return Promise.resolve(true);
      return rankingService
        .update({
          shopId,
          syteProduct,
          weights: weightsToSave as RankingStrategyTypes.RankingStrategyWeight[],
          variantId: testVariantId,
          type,
        })
        .then(() => true)
        .catch(() => {
          setHasError(true);
          return false;
        });
    },
    [shopId, syteProduct, testVariantId, type]
  );

  const persist = useMemo(
    () =>
      debounce((weightsToSave: RankingWeightRecord[]) => {
        sendUpdate(weightsToSave).finally(() => {
          pendingWeightsRef.current = null;
        });
      }, PERSIST_DEBOUNCE_MS),
    [sendUpdate]
  );

  useEffect(() => () => persist.cancel(), [persist]);

  const save = useCallback((): Promise<boolean> => {
    persist.cancel();
    const pending = pendingWeightsRef.current;
    if (!pending) return Promise.resolve(true);
    pendingWeightsRef.current = null;
    return sendUpdate(pending);
  }, [persist, sendUpdate]);

  useEffect(() => {
    onSaveReady?.(save);
  }, [save, onSaveReady]);

  // Same eligibility rule as the existing (non-wizard) ranking drawer's `useProductWeightsDraft`:
  // any data field with a numeric type is a valid ranking parameter. There's no `supportedFeatures`
  // gate there, so we match that here rather than inventing a stricter wizard-only rule.
  const numericDataFields = useMemo(
    () =>
      (dataFields ?? []).filter(field => {
        const dataFieldTypesSet = new Set(field.types);
        return (
          dataFieldTypesSet.has(FeedDataFieldType.Integer) ||
          dataFieldTypesSet.has(FeedDataFieldType.Float) ||
          dataFieldTypesSet.has(FeedDataFieldType.Long)
        );
      }),
    [dataFields]
  );

  const dataFieldDisplayByName = useMemo(
    () => new Map(numericDataFields.map(field => [field.name, field.displayName])),
    [numericDataFields]
  );

  const applyDraftChange = useCallback(
    (updater: (prev: RankingWeightRecord[]) => RankingWeightRecord[]) => {
      setDraftWeights(prev => {
        const next = updater(prev ?? []);
        pendingWeightsRef.current = next;
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const onWeightChange = useCallback(
    (index: number, weight: number) => {
      applyDraftChange(prev =>
        prev.map((row, rowIndex) => (rowIndex === index ? { ...row, weight } : row))
      );
    },
    [applyDraftChange]
  );

  // Only called from the "Add another change" composer's Apply button, so `newParameter` always
  // has a real field/name already chosen - unlike the earlier inline-row approach, a row never
  // enters `draftWeights` (and therefore never gets debounce-persisted) while incomplete.
  const onAddParameter = useCallback(
    (newParameter: NewRankingParameter) => {
      applyDraftChange(prev => [
        ...prev,
        { ...newParameter, enabled: true } as unknown as RankingWeightRecord,
      ]);
    },
    [applyDraftChange]
  );

  // Mirrors the existing (non-wizard) ranking drawer's delete rule: Syte Core fields are
  // permanent and never show a delete icon (enforced by the caller via `row.isSyteCore` before
  // this is even invoked) - any other row, added just now or already present, can be removed.
  const onRemoveParameter = useCallback(
    (index: number) => {
      applyDraftChange(prev => prev.filter((_, rowIndex) => rowIndex !== index));
    },
    [applyDraftChange]
  );

  // `RankingWeightRecord` (= `InterfaceToRecord<RankingStrategyWeight>`) is a pre-existing shared
  // type that widens every field to the union of all property types, so `row.field`/`row.name`
  // come back as `string | number | boolean` instead of their real types. Casting to the real
  // service type here (kept local to this file) avoids touching that shared utility.
  const typedControlWeights = (controlWeights ??
    []) as unknown as RankingStrategyTypes.RankingStrategyWeight[];
  const typedDraftWeights = (draftWeights ??
    []) as unknown as RankingStrategyTypes.RankingStrategyWeight[];

  const controlByField = useMemo(
    () => new Map(typedControlWeights.map(row => [row.field, row.weight])),
    [typedControlWeights]
  );

  const rows: RankingWeightRow[] = useMemo(
    () =>
      typedDraftWeights.map((row, index) => {
        const isSyteCore = syteCoreValuesSet.has(
          row.field as RankingStrategyTypes.SyteCoreRankingFields
        );
        const controlWeight = controlByField.get(row.field);
        const variantWeight = Number(row.weight);
        const delta =
          controlWeight === undefined ? undefined : variantWeight - Number(controlWeight);

        return {
          index,
          field: row.field,
          name: row.name || dataFieldDisplayByName.get(row.field) || row.field,
          order: row.order,
          sourceLabel: isSyteCore
            ? 'Syte Core'
            : dataFieldDisplayByName.get(row.field) ?? row.field,
          controlWeight,
          variantWeight,
          delta,
          isSyteCore,
        };
      }),
    [typedDraftWeights, controlByField, dataFieldDisplayByName]
  );

  // Fields already used by an existing row are shown but disabled in the composer, rather than
  // removed from the list outright - mirrors the existing drawer's `mappedSelectedDataFields`.
  const addableFields = useMemo(() => {
    const usedFields = new Set(typedDraftWeights.map(row => row.field));
    return numericDataFields.map(field => ({
      value: field.name,
      text: field.displayName,
      isDisabled: usedFields.has(field.name),
    }));
  }, [numericDataFields, typedDraftWeights]);

  const controlItems = useMemo(
    () =>
      typedControlWeights.map(row => ({
        label: row.name || dataFieldDisplayByName.get(row.field) || row.field,
        value: String(row.weight),
      })),
    [typedControlWeights, dataFieldDisplayByName]
  );

  // A row with no control counterpart is a brand-new parameter - always a change. Otherwise it's
  // a change only if the weight actually moved from control.
  const changeCount = useMemo(
    () => rows.filter(row => row.controlWeight === undefined || row.delta !== 0).length,
    [rows]
  );

  useEffect(() => {
    onChangeCountChange?.(changeCount);
  }, [changeCount, onChangeCountChange]);

  return {
    isLoading,
    hasError,
    rows,
    controlItems,
    changeCount,
    surfaceLabel: getProductName(surface),
    addableFields,
    onWeightChange,
    onAddParameter,
    onRemoveParameter,
  };
};
